import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { ordersAPI } from "../services/api";
import {
  Order,
  CreateOrderRequest,
  UpdateOrderStatusRequest,
  AssignDriverRequest,
} from "../types";
import { useApp } from "./AppContext";
import { queryClient, queryKeys, optimisticUpdates } from "../lib/queryClient";

interface OrderContextType {
  orders: Order[];
  currentOrder: Order | null;
  isLoading: boolean;
  error: string | null;

  // Order management
  createOrder: (orderData: CreateOrderRequest) => Promise<Order>;
  updateOrderStatus: (
    id: string,
    statusData: UpdateOrderStatusRequest
  ) => Promise<Order>;
  assignDriver: (id: string, assignData: AssignDriverRequest) => Promise<Order>;
  getOrder: (id: string) => Promise<Order>;
  getOrders: (filters?: any) => Promise<Order[]>;
  getOrdersByRole: (role: string, filters?: any) => Promise<Order[]>;

  // State management
  setCurrentOrder: (order: Order | null) => void;
  clearError: () => void;
  refreshOrders: () => Promise<void>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setGlobalError, setGlobalSuccess } = useApp();

  // Create new order
  const createOrder = useCallback(
    async (orderData: CreateOrderRequest): Promise<Order> => {
      try {
        setIsLoading(true);
        setError(null);

        const newOrder = await ordersAPI.createOrder(orderData);

        // Optimistically add to orders list
        optimisticUpdates.addListItem(queryKeys.orders.lists(), newOrder);

        setGlobalSuccess("Sipariş başarıyla oluşturuldu");

        return newOrder;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || "Sipariş oluşturulurken hata oluştu";
        setError(errorMessage);
        setGlobalError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setGlobalError, setGlobalSuccess]
  );

  // Update order status
  const updateOrderStatus = useCallback(
    async (
      id: string,
      statusData: UpdateOrderStatusRequest
    ): Promise<Order> => {
      try {
        setIsLoading(true);
        setError(null);

        const updatedOrder = await ordersAPI.updateOrderStatus(id, statusData);

        // Optimistically update order in list
        optimisticUpdates.updateListItem(
          queryKeys.orders.lists(),
          updatedOrder
        );

        // Update current order if it's the one being updated
        if (currentOrder?._id === id) {
          setCurrentOrder(updatedOrder);
        }

        setGlobalSuccess("Sipariş durumu başarıyla güncellendi");

        return updatedOrder;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error ||
          "Sipariş durumu güncellenirken hata oluştu";
        setError(errorMessage);
        setGlobalError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [currentOrder, setGlobalError, setGlobalSuccess]
  );

  // Assign driver to order
  const assignDriver = useCallback(
    async (id: string, assignData: AssignDriverRequest): Promise<Order> => {
      try {
        setIsLoading(true);
        setError(null);

        const updatedOrder = await ordersAPI.assignDriver(id, assignData);

        // Optimistically update order in list
        optimisticUpdates.updateListItem(
          queryKeys.orders.lists(),
          updatedOrder
        );

        // Update current order if it's the one being updated
        if (currentOrder?._id === id) {
          setCurrentOrder(updatedOrder);
        }

        setGlobalSuccess("Sürücü başarıyla atandı");

        return updatedOrder;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || "Sürücü atanırken hata oluştu";
        setError(errorMessage);
        setGlobalError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [currentOrder, setGlobalError, setGlobalSuccess]
  );

  // Get single order
  const getOrder = useCallback(
    async (id: string): Promise<Order> => {
      try {
        setIsLoading(true);
        setError(null);

        const order = await ordersAPI.getOrder(id);
        setCurrentOrder(order);

        return order;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error ||
          "Sipariş detayları yüklenirken hata oluştu";
        setError(errorMessage);
        setGlobalError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setGlobalError]
  );

  // Get orders list
  const getOrders = useCallback(
    async (filters?: any): Promise<Order[]> => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await ordersAPI.getOrders(filters);
        setOrders(response.orders);

        return response.orders;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || "Siparişler yüklenirken hata oluştu";
        setError(errorMessage);
        setGlobalError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setGlobalError]
  );

  // Get orders by role (for role-based filtering)
  const getOrdersByRole = useCallback(
    async (role: string, filters?: any): Promise<Order[]> => {
      try {
        setIsLoading(true);
        setError(null);

        const orders = await ordersAPI.getOrdersByRole(role, filters);
        setOrders(orders);

        return orders;
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || "Siparişler yüklenirken hata oluştu";
        setError(errorMessage);
        setGlobalError(errorMessage);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [setGlobalError]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh orders
  const refreshOrders = useCallback(async () => {
    try {
      await getOrders();
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists() });
    } catch (error) {
      console.error("Failed to refresh orders:", error);
    }
  }, [getOrders]);

  const value: OrderContextType = {
    orders,
    currentOrder,
    isLoading,
    error,
    createOrder,
    updateOrderStatus,
    assignDriver,
    getOrder,
    getOrders,
    getOrdersByRole,
    setCurrentOrder,
    clearError,
    refreshOrders,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

export const useOrders = (): OrderContextType => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error("useOrders must be used within an OrderProvider");
  }
  return context;
};

export default OrderContext;
